import {
  ApiResponse,
  EventModel,
  ApiSearchResponseModel,
  ExternalEvent,
  DirectionEnum,
  Bet,
  BetGroup,
  OfferDtoModel,
  EventOfferResponseModel,
  BulkUpdateQuotasRequestModel,
  ManuallyResolveRequestModel,
  BlockEventResponseModel,
  GenerateDailyPdfReadyResponseModel,
} from '../models';
import http from '../utils/http';
import { decamelize, decamelizeKeys } from 'humps';
import { RoundResponseModel } from '../models/responses/round-response.model';
import { BettingStatistic } from '../models/betting-statistic.model';
import { AutoLive } from '../models/responses/extra-controls-live.model';
import { SetConfigurationOnEventRequestModel } from '../models/requests/quotas-request.model';

/*
 * ============================================================
 * MULTI BACKEND CONFIGURATION
 * ============================================================
 */

interface BackendConfig {
  name: string;
  url: string;
  username?: string;
  password?: string;
}

interface BackendAuthToken {
  name: string;
  url: string;
  token: string;
}

const backendTokensStorageKey = 'admin-backend-auth-tokens';

/*
 * Read configured backends from .env.
 *
 * REACT_APP_BACKENDS:
 *
 * [
 *   {
 *     "name": "PET",
 *     "url": "https://petadmin.ddns.net",
 *     "username": "...",
 *     "password": "..."
 *   },
 *   {
 *     "name": "S1",
 *     "url": "https://s1-newadmin.premierbet.me/",
 *     "username": "...",
 *     "password": "..."
 *   }
 * ]
 */

function getBackends(): BackendConfig[] {
  const raw = process.env.REACT_APP_BACKENDS;

  if (!raw) {
    return [];
  }

  try {
    let value = raw.trim();

    /*
     * CRA can leave surrounding quotes around
     * the environment variable.
     */
    if (
      (value.startsWith("'") && value.endsWith("'")) ||
      (value.startsWith('"') && value.endsWith('"'))
    ) {
      value = value.substring(1, value.length - 1);
    }

    const parsed = JSON.parse(value);

    if (!Array.isArray(parsed)) {
      console.error(
        '[EVENTS] REACT_APP_BACKENDS is not an array',
      );
      return [];
    }

    return parsed.filter(
      (backend: BackendConfig) =>
        backend &&
        backend.name &&
        backend.url,
    );
  } catch (error) {
    console.error(
      '[EVENTS] Invalid REACT_APP_BACKENDS:',
      raw,
      error,
    );

    return [];
  }
}

/*
 * Read tokens generated during login.
 *
 * Example:
 *
 * [
 *   {
 *     "name": "PET",
 *     "url": "https://petadmin.ddns.net",
 *     "token": "..."
 *   },
 *   {
 *     "name": "S1",
 *     "url": "https://s1-newadmin.premierbet.me/",
 *     "token": "..."
 *   }
 * ]
 */

function getBackendAuthTokens(): BackendAuthToken[] {
  try {
    const stored = localStorage.getItem(
      backendTokensStorageKey,
    );

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/*
 * Only successfully authenticated backends
 * are returned.
 */
function getAuthenticatedBackends(): BackendAuthToken[] {
  return getBackendAuthTokens().filter(
    (backend) =>
      backend &&
      backend.name &&
      backend.url &&
      backend.token,
  );
}

function normalizeApiBase(apiBase: string): string {
  return apiBase.replace(/\/+$/, '');
}

function getBackendUrl(
  backend: BackendAuthToken,
  path: string,
): string {
  return `${normalizeApiBase(backend.url)}${path}`;
}

/*
 * Find backend by name.
 */
function getBackendByName(
  backendName: string,
): BackendAuthToken | undefined {
  return getAuthenticatedBackends().find(
    (backend) => backend.name === backendName,
  );
}

/*
 * Find backend by URL.
 */
function getBackendByUrl(
  apiBase: string,
): BackendAuthToken | undefined {
  const normalized = normalizeApiBase(apiBase);

  return getAuthenticatedBackends().find(
    (backend) =>
      normalizeApiBase(backend.url) === normalized,
  );
}

/*
 * Resolve backend from either:
 *
 * - backendName
 * - apiBase
 * - first authenticated backend
 */
function resolveBackend(
  apiBase?: string,
  backendName?: string,
): BackendAuthToken | undefined {
  if (backendName) {
    const backend = getBackendByName(backendName);

    if (backend) {
      return backend;
    }
  }

  if (apiBase) {
    const backend = getBackendByUrl(apiBase);

    if (backend) {
      return backend;
    }

    /*
     * apiBase may be supplied without trailing slash
     * or with a path accidentally included.
     */
    const normalized = normalizeApiBase(apiBase);

    const byPrefix = getAuthenticatedBackends().find(
      (backend) =>
        normalized.startsWith(
          normalizeApiBase(backend.url),
        ),
    );

    if (byPrefix) {
      return byPrefix;
    }
  }

  return getAuthenticatedBackends()[0];
}

/*
 * Resolve URL for a concrete backend.
 *
 * IMPORTANT:
 * We no longer concatenate apiBase with
 * REACT_APP_API_URL.
 */
function resolveUrl(
  path: string,
  apiBase?: string,
  backendName?: string,
): string {
  const backend = resolveBackend(
    apiBase,
    backendName,
  );

  if (!backend) {
    throw new Error(
      '[EVENTS] No authenticated backend available',
    );
  }

  return getBackendUrl(backend, path);
}

/*
 * Axios config used for every backend request.
 *
 * We explicitly provide Auth-Token here as well as
 * the backend marker used by utils/http.ts.
 */
function getBackendRequestConfig(
  backend: BackendAuthToken,
  extra: any = {},
): any {
  return {
    ...extra,
    withCredentials: true,
    backend: backend.name,
    headers: {
      ...(extra.headers || {}),
      'Auth-Token': backend.token,
    },
  };
}

/*
 * ============================================================
 * EVENT HELPERS
 * ============================================================
 */

function getUniqueActionTargets(
  targets: NonNullable<EventModel['actionTargets']>,
): NonNullable<EventModel['actionTargets']> {
  return targets.filter((target, index, self) => {
    const apiBase = target.apiBase || '';

    return (
      self.findIndex(
        (item) =>
          (item.apiBase || '') === apiBase,
      ) === index
    );
  });
}

function addActionTarget(
  event: EventModel,
  backend: BackendAuthToken,
): EventModel {
  const apiBase = normalizeApiBase(backend.url);

  return {
    ...event,

    /*
     * Used by EventRow.tsx:
     *
     * [PET]
     * [S1]
     */
    backendName: backend.name,

    /*
     * Backend URL is kept on the event.
     */
    backendUrl: apiBase,

    /*
     * Event actions use this backend.
     */
    actionTargets: [
      {
        id: event.id,
        apiBase,
      },
    ],
  } as EventModel;
}

function addBackendInformation(
  events: EventModel[],
  backend: BackendAuthToken,
): EventModel[] {
  return events.map((event) =>
    addActionTarget(event, backend),
  );
}

/*
 * ============================================================
 * EVENTS
 * ============================================================
 */

export async function serviceGetEvents(
  parameters?: { [key: string]: any },
  limit: number = 50,
  page: number = 0,
  orderBy?: string,
  direction?: DirectionEnum,
): Promise<ApiSearchResponseModel<EventModel[]>> {
  const requestedPage = Math.max(
    Number(page) || 0,
    0,
  );

  const pageSize = Math.max(
    Number(limit) || 50,
    1,
  );

  /*
   * Globalna paginacija preko svih backenda.
   *
   * page 0, limit 100:
   *   PET -> 100
   *   S1  -> 100
   *
   * page 1, limit 100:
   *   PET -> 200
   *   S1  -> 200
   *
   * Ovo je potrebno jer backendovi imaju odvojene rezultate.
   */
  const fetchLimit =
    (requestedPage + 1) * pageSize;

  /*
   * Ako sorting nije eksplicitno poslan,
   * pokušaj ga uzeti iz parameters.
   */
  const effectiveOrderBy =
    orderBy ||
    parameters?.orderBy ||
    'startTime';

  const effectiveDirection =
    direction ||
    parameters?.direction ||
    DirectionEnum.asc;

  /*
   * Backend uvijek dobija page=0.
   *
   * Globalni page radimo tek nakon što
   * spojimo rezultate svih backenda.
   */
  const reqData: Record<string, any> = {
    ...(parameters || {}),
    page: 0,
    limit: fetchLimit,
    isPagination: true,

    orderBy: decamelize(
      effectiveOrderBy,
    ),

    direction:
      effectiveDirection,
  };

  const decReqData =
    decamelizeKeys(reqData);

  const backends =
    getAuthenticatedBackends();

  if (!backends.length) {
    return {
      data: [],
      rowcount: 0,
      totalCount: 0,
      status: 'success',
    } as ApiSearchResponseModel<EventModel[]>;
  }

  /*
   * Svi backendovi se pozivaju paralelno.
   */
  const responses = await Promise.all(
    backends.map(async (backend) => {
      const backendParams = {
        ...decReqData,

        /*
         * Za svaki backend uvijek page 0.
         */
        page: 0,

        /*
         * Npr. page 1 / limit 100 -> 200.
         */
        limit: fetchLimit,
      };

      const urlParams =
        new URLSearchParams(
          Object.entries(
            backendParams,
          ).map(
            ([key, value]) => [
              key,
              String(value),
            ],
          ),
        ).toString();

      try {
        const response =
          await http.get(
            getBackendUrl(
              backend,
              `/api/events/search?${urlParams}`,
            ),
            getBackendRequestConfig(
              backend,
              {
                headers: {
                  errorAlert: false,
                },
              },
            ),
          ) as unknown as ApiSearchResponseModel<EventModel[]>;

        const events =
          Array.isArray(response.data)
            ? response.data
            : [];

        return {
          backend,

          events: events.map(
            (event) => ({
              ...event,

              backendName:
                backend.name,

              backendUrl:
                normalizeApiBase(
                  backend.url,
                ),

              /*
               * ID mora biti unique preko svih backenda.
               */
              _entityId:
                `${backend.name}:${event.id}`,

              actionTargets: [
                {
                  id: event.id,
                  apiBase:
                    normalizeApiBase(
                      backend.url,
                    ),
                },
              ],
            }),
          ),

          rowcount:
            Number(
              response.rowcount,
            ) || 0,
        };
      } catch (error) {
        console.error(
          `[EVENTS] ${backend.name} search failed`,
          error,
        );

        return {
          backend,
          events: [],
          rowcount: 0,
        };
      }
    }),
  );

  /*
   * ============================================================
   * TOTAL COUNT
   * ============================================================
   *
   * Npr:
   *
   * PET = 350
   * S1  = 270
   *
   * GLOBAL = 620
   */
  const totalRowCount =
    responses.reduce(
      (total, response) =>
        total + response.rowcount,
      0,
    );

  /*
   * ============================================================
   * MERGE
   * ============================================================
   */
  const allEvents: EventModel[] =
    responses.flatMap(
      (response) =>
        response.events,
    );

  /*
   * ============================================================
   * GLOBAL SORT
   * ============================================================
   *
   * Backendi su zasebno sortirani.
   * Zato nakon merge-a moramo ponovo sortirati
   * kompletan skup.
   */
  const sortFieldMap: Record<
    string,
    string
  > = {
    created: 'created',

    start_time: 'startTime',
    startTime: 'startTime',

    stake: 'stake',
    amount: 'stake',

    gain: 'gain',

    code: 'code',

    id_status: 'idStatus',
    idStatus: 'idStatus',

    id: 'id',
  };


  const sortField: string =
  sortFieldMap[
    String(effectiveOrderBy)
  ] ||
  String(effectiveOrderBy);

allEvents.sort(
  (a: any, b: any): number => {
    // 1. Datum / vrijeme
    const dateA = new Date(
      a.startTime,
    ).getTime();

    const dateB = new Date(
      b.startTime,
    ).getTime();

    if (dateA !== dateB) {
      return effectiveDirection ===
        DirectionEnum.asc
        ? dateA - dateB
        : dateB - dateA;
    }

    // 2. Prvi tim
    const participant1A = String(
      a.participant1?.name ??
      '',
    );

    const participant1B = String(
      b.participant1?.name ??
      '',
    );

    const participant1Compare =
      participant1A.localeCompare(
        participant1B,
        undefined,
        {
          sensitivity: 'base',
        },
      );

    if (participant1Compare !== 0) {
      return effectiveDirection ===
        DirectionEnum.asc
        ? participant1Compare
        : -participant1Compare;
    }

    // 3. Drugi tim
    const participant2A = String(
      a.participant2?.name ??
      '',
    );

    const participant2B = String(
      b.participant2?.name ??
      '',
    );

    const participant2Compare =
      participant2A.localeCompare(
        participant2B,
        undefined,
        {
          sensitivity: 'base',
        },
      );

    if (participant2Compare !== 0) {
      return effectiveDirection ===
        DirectionEnum.asc
        ? participant2Compare
        : -participant2Compare;
    }

    return 0;
  },
);

  /*
   * ============================================================
   * GLOBAL PAGINATION
   * ============================================================
   *
   * limit = 100
   *
   * page 0:
   *   0 - 99
   *
   * page 1:
   *   100 - 199
   *
   * page 2:
   *   200 - 299
   */
  const start =
    requestedPage * pageSize;

  const end =
    start + pageSize;

  const paginatedEvents =
    allEvents.slice(
      start,
      end,
    );

  /*
   * Frontend dobija SAMO evente trenutne stranice.
   */
  return {
    data: paginatedEvents,

    /*
     * Stvarni ukupan broj eventa
     * preko svih backenda.
     */
    rowcount:
      totalRowCount,

    totalCount:
      totalRowCount,

    status: 'success',
  } as ApiSearchResponseModel<EventModel[]>;
}

/*
 * Omni search across all authenticated backends.
 */
export async function serviceSearchEventsByOmniSearch(
  searchValue: string,
  idCompetitionTypes?: number,
): Promise<EventModel[]> {
  const backends =
    getAuthenticatedBackends();

  if (!backends.length) {
    return [];
  }

  const responses = await Promise.all(
    backends.map(async (backend) => {
      try {
        const response =
          await http.get(
            getBackendUrl(
              backend,
              '/api/events/search',
            ),
            getBackendRequestConfig(
              backend,
              {
                params: {
                  omnisearch: searchValue,
                  idCompetitionTypes,
                  isPagination: false,
                },
                headers: {
                  errorAlert: false,
                },
              },
            ),
          );

        return (
          Array.isArray(response.data)
            ? response.data
            : []
        ).map((event: EventModel) =>
          addActionTarget(
            event,
            backend,
          ),
        );
      } catch (error) {
        console.error(
          `[EVENTS] ${backend.name} omni search failed`,
          error,
        );

        return [];
      }
    }),
  );

  return responses.flat();
}

/*
 * Update event.
 *
 * apiBase is normally event.backendUrl.
 */
export async function serviceUpdateEvents(
  eventData: Partial<EventModel>,
  backendName: string,
): Promise<any> {
  const backend =
    getBackendByName(backendName);

  if (!backend) {
    throw new Error(
      `[EVENTS] Backend "${backendName}" not found`,
    );
  }

  return await http.patch(
    getBackendUrl(
      backend,
      `/api/events/edit/${eventData.id}`,
    ),
    eventData,
    getBackendRequestConfig(
      backend,
      {
        headers: {
          successAlert: true,
        },
      },
    ),
  );
} 
/*
 * Block/unblock event.
 */
export async function serviceBlockEvent(
  eventId: number,
  block?: boolean,
  blockFeed?: boolean,
  apiBase: string = '',
): Promise<BlockEventResponseModel> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for block event',
    );
  }

  const response =
    await http.patch(
      getBackendUrl(
        backend,
        `/api/events/block-event/${eventId}`,
      ),
      {
        block,
        blockFeed,
      },
      getBackendRequestConfig(
        backend,
        {
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

/*
 * External events.
 *
 * No event backend context exists here,
 * therefore fetch from all authenticated backends.
 */
export async function serviceGetExternalEvents(): Promise<ExternalEvent[]> {
  const backends =
    getAuthenticatedBackends();

  if (!backends.length) {
    return [];
  }

  const responses = await Promise.all(
    backends.map(async (backend) => {
      try {
        const response =
          await http.get(
            getBackendUrl(
              backend,
              '/api/events/external',
            ),
            getBackendRequestConfig(
              backend,
              {
                headers: {
                  errorAlert: false,
                },
              },
            ),
          );

        return Array.isArray(response.data)
          ? response.data.map((event: ExternalEvent) => ({
              ...event,
              backendName: backend.name,
              backendUrl: normalizeApiBase(backend.url),
            }))
          : [];
      } catch (error) {
        console.error(
          `[EVENTS] ${backend.name} external events failed`,
          error,
        );

        return [];
      }
    }),
  );

  return responses.flat();
}

/*
 * Pair event.
 *
 * If apiBase is supplied, only that backend
 * is modified.
 */
export async function servicePairEvent(
  eventId: number,
  hash: string,
  apiBase: string = '',
) {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for pair event',
    );
  }

  const response: ApiResponse<any> =
    await http.put(
      getBackendUrl(
        backend,
        '/api/events/pair',
      ),
      {
        hashMap: {
          [eventId]: hash,
        },
      },
      getBackendRequestConfig(
        backend,
        {
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

/*
 * Unpair event.
 */
export async function serviceUnpairEvent(
  eventId: number,
  apiBase: string = '',
) {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for unpair event',
    );
  }

  const response: ApiResponse<any> =
    await http.put(
      getBackendUrl(
        backend,
        '/api/events/unpair',
      ),
      {
        eventIds: [eventId],
      },
      getBackendRequestConfig(
        backend,
        {
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

/*
 * ============================================================
 * ROUNDS
 * ============================================================
 *
 * Rounds are fetched from ALL authenticated backends.
 */

export async function serviceGetRounds(): Promise<
  RoundResponseModel[]
> {
  const backends =
    getAuthenticatedBackends();

  if (!backends.length) {
    return [];
  }

  const responses = await Promise.all(
    backends.map(async (backend) => {
      try {
        const response:
          ApiResponse<RoundResponseModel[]> =
          await http.get(
            getBackendUrl(
              backend,
              '/api/static/date/rounds',
            ),
            getBackendRequestConfig(
              backend,
              {
                headers: {
                  errorAlert: false,
                },
              },
            ),
          );

        return Array.isArray(response.data)
          ? response.data
          : [];
      } catch (error) {
        console.error(
          `[ROUNDS] ${backend.name} request failed`,
          error,
        );

        return [];
      }
    }),
  );

  /*
   * Remove duplicate round values.
   */
  const allRounds =
    responses.flat();

  const uniqueRounds =
    allRounds.filter(
      (round, index, array) =>
        index ===
        array.findIndex(
          (item) =>
            JSON.stringify(item) ===
            JSON.stringify(round),
        ),
    );

  return uniqueRounds;
}

/*
 * ============================================================
 * EVENT OFFER
 * ============================================================
 */

export async function serviceGetEventOffer(
  eventId: number,
  apiBase: string = '',
): Promise<OfferDtoModel> {
  const eventOffer =
    await serviceFetchEventOffer(
      eventId,
      apiBase,
    );

  const betGroups =
    eventOffer.betGroups;

  const betsByCode =
    getGroupsByCode(
      betGroups,
    );

  return {
    betsByCode,
    betGroups,
    referralBets:
      eventOffer.referralBets,
    referralArrays:
      eventOffer.referralArrays,
  };
}


function getGroupsByCode(
  betGroups: BetGroup[],
): any {
  return betGroups.reduce(
    (
      res: any,
      betGroup,
    ) => {
      const items =
        betGroup.bets.reduce(
          (
            obj: Record<string, Bet>,
            bet,
          ) => {
            obj[bet.code] = {
              ...bet,
              groupId: betGroup.id,
            };

            return obj;
          },
          {},
        );

      return {
        ...res,
        ...items,
      };
    },
    {},
  );
}

export async function serviceBulkUpdateQuotas(
  reqData: BulkUpdateQuotasRequestModel,
  apiBase: string = '',
): Promise<Partial<OfferDtoModel>> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for quota generation',
    );
  }

  const response =
    await http.put(
      getBackendUrl(
        backend,
        '/api/events/quotas/generate',
      ),
      reqData,
      getBackendRequestConfig(
        backend,
      ),
    );

  const result =
    response.data;

  return {
    betsByCode:
      getGroupsByCode(
        result.betGroups,
      ),
    betGroups:
      result.betGroups,
  };
}

export async function serviceFetchEventOffer(
  eventId: number,
  apiBase: string = '',
): Promise<EventOfferResponseModel> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for event offer',
    );
  }

  const response:
    ApiResponse<EventOfferResponseModel> =
    await http.get(
      getBackendUrl(
        backend,
        `/api/events/get-referent-quotas/${eventId}`,
      ),
      getBackendRequestConfig(
        backend,
      ),
    );

  return response.data;
}

/*
 * ============================================================
 * DAILY PDF
 * ============================================================
 */

export async function serviceFetchDailyEventsPdfIsReady(
  apiBase: string = '',
): Promise<boolean> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    return false;
  }

  const response:
    ApiResponse<GenerateDailyPdfReadyResponseModel> =
    await http.get(
      getBackendUrl(
        backend,
        '/api/events/generate-daily-pdf-ready',
      ),
      getBackendRequestConfig(
        backend,
      ),
    );

  return !!response?.data
    ?.generateDailyPdfReady;
}

export async function serviceUpdateDailyEventsPdfIsReady(
  isReady: boolean,
  apiBase: string = '',
) {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for daily PDF update',
    );
  }

  const response =
    await http.post(
      getBackendUrl(
        backend,
        '/api/events/generate-daily-pdf-ready',
      ),
      {
        ready: isReady,
      },
      getBackendRequestConfig(
        backend,
      ),
    );

  return response.data;
}

/*
 * ============================================================
 * EVENT OFFER UPDATE
 * ============================================================
 */

export async function serviceUpdateEventOffer(
  eventId: number,
  offer: { [key: string]: any },
  apiBase: string = '',
) {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for offer update',
    );
  }

  const response =
    await http.put(
      getBackendUrl(
        backend,
        `/api/events/offer/${eventId}`,
      ),
      offer,
      getBackendRequestConfig(
        backend,
        {
          params: {
            adminUpdate: true,
          },
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

/*
 * ============================================================
 * EXTERNAL LIVE EVENTS
 * ============================================================
 */

export async function serviceFetchExternalLiveEvents(
  apiBase: string = '',
): Promise<ExternalEvent[]> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    return [];
  }

  const response:
    ApiResponse<any> =
    await http.get(
      getBackendUrl(
        backend,
        '/api/events/live/external',
      ),
      getBackendRequestConfig(
        backend,
      ),
    );

  return response.data;
}

export async function serviceStartExternalLiveEvent(
  event: ExternalEvent,
  apiBase: string = '',
): Promise<ExternalEvent[]> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for external live event',
    );
  }

  const response:
    ApiResponse<any> =
    await http.post(
      getBackendUrl(
        backend,
        '/api/events/live/external/start',
      ),
      {
        hash: event.hash,
      },
      getBackendRequestConfig(
        backend,
        {
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

export async function serviceStartStopAllOdds(
  status: boolean,
  apiBase: string = '',
) {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for odds status',
    );
  }

  const response:
    ApiResponse<any> =
    await http.post(
      getBackendUrl(
        backend,
        `/api/events/all-offers-status/${+status}`,
      ),
      {},
      getBackendRequestConfig(
        backend,
        {
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

export async function serviceResetLiveExternalBuffer(
  apiBase: string = '',
): Promise<any> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for buffer reset',
    );
  }

  const response =
    await http.delete(
      getBackendUrl(
        backend,
        '/api/events/live/external/reset-buffer',
      ),
      getBackendRequestConfig(
        backend,
        {
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

/*
 * ============================================================
 * BET STATISTICS
 * ============================================================
 */

export async function serviceGetBetStatistics(
  parameters: { [key: string]: any },
  apiBase: string = '',
): Promise<BettingStatistic[]> {
  const reqData: {
    [key: string]: any
  } = {
    ...parameters,
    isPagination: false,
  };

  const decReqData =
    decamelizeKeys(
      reqData,
    );

  const urlParams =
    new URLSearchParams(
      Object.entries(
        decReqData,
      ).map(
        ([key, value]) => [
          key,
          String(value),
        ],
      ),
    ).toString();

  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    return [];
  }

  const response:
    ApiResponse<BettingStatistic[]> =
    await http.get(
      getBackendUrl(
        backend,
        `/api/events/eds?${urlParams}`,
      ),
      getBackendRequestConfig(
        backend,
      ),
    );

  return response.data;
}

/*
 * ============================================================
 * BET SEARCH
 * ============================================================
 */

export async function serviceSearchTips(
  omniSearch: string,
  apiBase: string = '',
): Promise<any> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    return [];
  }

  const response:
    ApiResponse<any> =
    await http.get(
      getBackendUrl(
        backend,
        '/api/events/bet/search',
      ),
      getBackendRequestConfig(
        backend,
        {
          params: {
            omnisearch: omniSearch,
          },
        },
      ),
    );

  return response.data;
}

/*
 * ============================================================
 * EVENT RESOLVE / RESULT
 * ============================================================
 */

export async function serviceManuallyResolveEvent(
  eventId: number,
  requestData: ManuallyResolveRequestModel,
  apiBase: string = '',
) {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for resolve event',
    );
  }

  const response:
    ApiResponse<any> =
    await http.put(
      getBackendUrl(
        backend,
        `/api/events/resolve/${eventId}`,
      ),
      {
        ...requestData,
      },
      getBackendRequestConfig(
        backend,
        {
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

export async function serviceCancelResult(
  idEvent: number,
  backendUrl: string,
) {
  const response = await http.post(
    `${backendUrl}/api/sportsbet/cancel-result-by-event`,
    { idEvent },
    {
      headers: {
        successAlert: true,
      },
    },
  );

  return response.data;
}

/*
 * ============================================================
 * PUBLISH REVISION
 * ============================================================
 */

export async function servicePublishRevision(
  apiBase: string = '',
) {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for publish revision',
    );
  }

  const response =
    await http.get(
      getBackendUrl(
        backend,
        '/api/events/publish-revisions',
      ),
      getBackendRequestConfig(
        backend,
        {
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

/*
 * ============================================================
 * GENERATE EVENTS
 * ============================================================
 */

export async function serviceGenerateDailyEvents(
  idCompetitionType: number,
  params?: { [key: string]: any },
  apiBase: string = '',
): Promise<any> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for generate daily events',
    );
  }

  const response =
    await http.get(
      getBackendUrl(
        backend,
        `/api/events/generate_daily_events/${idCompetitionType}`,
      ),
      getBackendRequestConfig(
        backend,
        {
          params: {
            ...(params || {}),
          },
        },
      ),
    );

  return response.data;
}

export async function serviceGenerateCsvForEvents(
  apiBase: string = '',
): Promise<any> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for CSV events',
    );
  }

  const response =
    await http.get(
      getBackendUrl(
        backend,
        '/api/events/generate_csv_for_ttx',
      ),
      getBackendRequestConfig(
        backend,
      ),
    );

  return response.data;
}

export async function serviceGenerateEventsForList(
  idCompetitionType: number,
  params?: { [key: string]: any },
  apiBase: string = '',
): Promise<any> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for event list generation',
    );
  }

  const response =
    await http.get(
      getBackendUrl(
        backend,
        `/api/events/generate-events-for-list/${idCompetitionType}`,
      ),
      getBackendRequestConfig(
        backend,
        {
          params: {
            ...(params || {}),
          },
        },
      ),
    );

  return response.data;
}

export async function serviceGenerateCsvForResults(
  apiBase: string = '',
): Promise<any> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for CSV results',
    );
  }

  const response =
    await http.get(
      getBackendUrl(
        backend,
        '/api/events/generate_csv_for_results',
      ),
      getBackendRequestConfig(
        backend,
      ),
    );

  return response.data;
}

/*
 * ============================================================
 * LIVE STATUS
 * ============================================================
 */

export async function serviceEventsLiveStatus(
  apiBase: string = '',
): Promise<any> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    return null;
  }

  const response =
    await http.get(
      getBackendUrl(
        backend,
        '/api/events/live/status',
      ),
      getBackendRequestConfig(
        backend,
      ),
    );

  return response.data;
}

export async function serviceSetAutoStartLive(
  autoStart: boolean,
  apiBase: string = '',
) {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for auto live',
    );
  }

  const response:
    ApiResponse<any> =
    await http.post(
      getBackendUrl(
        backend,
        '/api/events/live/start/auto',
      ),
      {
        autoStart,
      },
      getBackendRequestConfig(
        backend,
      ),
    );

  return response.data;
}

export async function serviceGetAutoStart(
  apiBase: string = '',
) {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    return null;
  }

  const response:
    ApiResponse<AutoLive> =
    await http.get(
      getBackendUrl(
        backend,
        '/api/events/live/start/auto',
      ),
      getBackendRequestConfig(
        backend,
      ),
    );

  return response.data;
}

/*
 * ============================================================
 * EVENT CONFIGURATION
 * ============================================================
 */

export async function getEventConfigurationService(
  idEvent: number,
  apiBase: string = '',
): Promise<any> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    return [];
  }

  const response:
    ApiResponse<any[]> =
    await http.get(
      getBackendUrl(
        backend,
        '/api/events/quota/management/event/percentage/all',
      ),
      getBackendRequestConfig(
        backend,
        {
          params: {
            idEvent,
          },
        },
      ),
    );

  return response.data;
}

export async function serviceSetOrEditConfigurationOnEvent(
  data: SetConfigurationOnEventRequestModel,
  apiBase: string = '',
) {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for event configuration',
    );
  }

  const response:
    ApiResponse<any> =
    await http.post(
      getBackendUrl(
        backend,
        '/api/events/quota/management/event/percentage',
      ),
      {
        ...data,
      },
      getBackendRequestConfig(
        backend,
        {
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

export async function deleteEventConfigurationService(
  idQuotaManagement: number,
  apiBase: string = '',
): Promise<any> {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for event configuration delete',
    );
  }

  const response =
    await http.delete(
      getBackendUrl(
        backend,
        '/api/events/quota/management/event/percentage',
      ),
      getBackendRequestConfig(
        backend,
        {
          params: {
            idQuotaManagement,
          },
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

export async function applyEventConfiguration(
  idEvent: number,
  apiBase: string = '',
) {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for applying event configuration',
    );
  }

  const response =
    await http.put(
      getBackendUrl(
        backend,
        '/api/events/quotas/config/generate',
      ),
      {
        idEvent,
      },
      getBackendRequestConfig(
        backend,
      ),
    );

  const result =
    response.data;

  return {
    betsByCode:
      getGroupsByCode(
        result.betGroups,
      ),
    betGroups:
      result.betGroups,
  };
}

/*
 * ============================================================
 * PARSER COMMANDS
 * ============================================================
 */

export async function parserCommandsService(
  parser: string,
  command: string,
  apiBase: string = '',
) {
  const backend =
    resolveBackend(apiBase);

  if (!backend) {
    throw new Error(
      '[EVENTS] No backend available for parser command',
    );
  }

  const response:
    ApiResponse<any> =
    await http.post(
      getBackendUrl(
        backend,
        '/api/events/parsers/command',
      ),
      {
        parser,
        command,
      },
      getBackendRequestConfig(
        backend,
        {
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}
